import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { DependencyVisualizer } from '../utils/dependency-visualizer';
import pc from 'picocolors';

// Helper function to format duration
function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  } else if (minutes < 480) { // Less than 8 hours
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
  } else {
    const days = Math.floor(minutes / 480); // 8-hour workday
    const remainingHours = Math.floor((minutes % 480) / 60);
    return remainingHours > 0 ? `${days} days ${remainingHours} hours` : `${days} days`;
  }
}

export function createTaskCommand(): Command {
  const command = new Command('task');
  
  command
    .description('Task state management for hooks integration')
    .alias('t');

  // List current and recent tasks
  command
    .command('list')
    .description('List current and recent tasks')
    .option('-n, --number <number>', 'Number of recent tasks to show', '10')
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const state = await manager.getTaskState();
      
      if (!state) {
        logger.info('No task history found. Use "workflow task start" to begin.');
        return;
      }
      
      // Show current task
      if (state.current) {
        console.log(pc.bold('\n📋 Current Task:'));
        console.log(`Task: ${pc.cyan(state.current.task)}`);
        console.log(`Phase: ${pc.yellow(state.current.phase)}`);
        console.log(`Priority: ${state.current.priority}`);
        console.log(`Started: ${new Date(state.current.startTime).toLocaleTimeString()}`);
      } else {
        console.log(pc.yellow('\nNo active task'));
      }
      
      // Show recent history
      if (state.history && state.history.length > 0) {
        console.log(pc.bold('\n📊 Recent Tasks:'));
        const recentTasks = state.history.slice(-parseInt(options.number));
        
        recentTasks.forEach(task => {
          const icon = task.status === 'completed' ? '✅' : 
                      task.status === 'failed' ? '❌' : '⚡';
          const duration = Math.round(task.duration / 60000); // minutes
          console.log(`${icon} ${task.task} (${duration}min)`);
        });
      }
    }));

  // Show task status
  command
    .command('status')
    .description('Show current task status')
    .action(withErrorHandling(async () => {
      const manager = new WorkflowManager();
      const state = await manager.getTaskState();
      
      if (!state || !state.current) {
        console.log(pc.yellow('No active task'));
        return;
      }
      
      console.log(pc.bold('\n📊 Current Task Status:'));
      console.log(`Task: ${state.current.task}`);
      console.log(`Phase: ${state.current.phase}`);
      console.log(`Priority: ${state.current.priority}`);
      console.log(`Started: ${new Date(state.current.startTime).toLocaleTimeString()}`);
      
      const duration = Date.now() - new Date(state.current.startTime).getTime();
      const minutes = Math.floor(duration / 60000);
      console.log(`Duration: ${minutes} minutes`);
      
      if (state.current.context) {
        console.log('\nContext:', state.current.context);
      }
    }));

  // Start a new task
  command
    .command('start')
    .description('Start a new task')
    .argument('<task>', 'Task description')
    .option('-p, --priority <priority>', 'Task priority (high/medium/low)', 'medium')
    .option('--phase <phase>', 'Starting phase', 'understanding')
    .option('--estimate', 'Show time estimation', false)
    .action(withErrorHandling(async (taskDesc, options) => {
      const manager = new WorkflowManager();
      
      // Show time estimation if requested
      if (options.estimate) {
        const estimation = await manager.estimateTaskTime(taskDesc, options.priority);
        if (estimation) {
          console.log(pc.bold('\n⏱️  Task Analysis:'));
          console.log(`Complexity: ${estimation.complexity.score}/100`);
          console.log(`Estimated time: ${formatDuration(estimation.complexity.estimatedTime)}`);
          
          if (estimation.complexity.factors.length > 0) {
            console.log(pc.dim('\nFactors:'));
            estimation.complexity.factors.forEach(factor => {
              console.log(pc.dim(`  • ${factor}`));
            });
          }
          
          if (estimation.workflowRecommendation) {
            console.log(pc.bold('\n🔄 Recommended workflow: ') + pc.cyan(estimation.workflowRecommendation.type));
            console.log(pc.dim(estimation.workflowRecommendation.reason));
          }
          console.log(); // Empty line
        }
      }
      
      const result = await manager.startTask({
        task: taskDesc,
        priority: options.priority,
        phase: options.phase
      });
      
      if (result.success) {
        console.log(pc.green(`✅ Started task: ${taskDesc}`));
        
        // Always show basic time estimation
        const estimation = await manager.estimateTaskTime(taskDesc, options.priority);
        if (estimation?.complexity?.estimatedTime) {
          console.log(pc.dim(`⏱️  Estimated time: ${formatDuration(estimation.complexity.estimatedTime)}`));
        }
        
        console.log(pc.dim('Use "workflow task status" to track progress'));
      } else {
        console.log(pc.red(`Failed to start task: ${result.error}`));
      }
    }));

  // Complete current task
  command
    .command('complete')
    .description('Mark current task as complete')
    .option('-n, --notes <notes>', 'Completion notes')
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const result = await manager.completeCurrentTask(options.notes);
      
      if (result.success) {
        console.log(pc.green('✅ Task completed successfully'));
        
        // Get next recommended task
        const recommendations = await manager.recommendNextTask();
        if (recommendations && recommendations.length > 0) {
          console.log(pc.bold('\n🎯 Next recommended task:'));
          const next = recommendations[0];
          console.log(`${next.task} (Priority: ${next.priority})`);
          console.log(pc.dim(`Reason: ${next.reason}`));
          console.log(pc.dim(`Estimated duration: ${next.estimatedDuration} minutes`));
        }
      } else {
        console.log(pc.red(`Failed to complete task: ${result.error}`));
      }
    }));

  // Recommend next task
  command
    .command('recommend')
    .description('Get task recommendations')
    .option('-n, --number <number>', 'Number of recommendations', '3')
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const recommendations = await manager.recommendNextTask();
      
      if (!recommendations || recommendations.length === 0) {
        console.log(pc.yellow('No task recommendations available'));
        return;
      }
      
      console.log(pc.bold('\n🎯 Recommended Tasks:'));
      recommendations.slice(0, parseInt(options.number)).forEach((rec, index) => {
        console.log(`\n${index + 1}. ${pc.cyan(rec.task)}`);
        console.log(`   Priority: ${rec.priority}`);
        console.log(`   Reason: ${rec.reason}`);
        if (rec.estimatedDuration) {
          console.log(`   Estimated: ${rec.estimatedDuration} minutes`);
        }
      });
    }));
  
  // Split task into subtasks
  command
    .command('split')
    .description('Split a task into smaller subtasks')
    .argument('<task>', 'Task description to split')
    .option('--max-hours <hours>', 'Maximum hours per subtask', '4')
    .option('--max-depth <depth>', 'Maximum nesting depth', '3')
    .option('--no-tests', 'Exclude test tasks')
    .option('--no-docs', 'Exclude documentation tasks')
    .option('--strategy <strategy>', 'Split strategy (balanced/sequential/parallel)', 'balanced')
    .option('--format <format>', 'Output format (text/json/gantt/tree/graph/mermaid)', 'text')
    .option('--save', 'Save subtasks to workflow', false)
    .action(withErrorHandling(async (taskDesc, options) => {
      const manager = new WorkflowManager();
      
      // Split the task
      const splitResult = await manager.splitTask(taskDesc, {
        maxHours: parseInt(options.maxHours),
        maxDepth: parseInt(options.maxDepth),
        includeTests: options.tests !== false,
        includeDocs: options.docs !== false,
        strategy: options.strategy
      });
      
      if (!splitResult) {
        console.log(pc.red('Failed to split task'));
        return;
      }
      
      // Display results based on format
      if (options.format === 'json') {
        console.log(JSON.stringify(splitResult, null, 2));
        return;
      }
      
      // Visualization formats
      if (options.format === 'tree') {
        console.log(DependencyVisualizer.visualizeTree(splitResult.subtasks, {
          showCriticalPath: true,
          criticalPath: splitResult.criticalPath
        }));
        return;
      }
      
      if (options.format === 'graph') {
        console.log(DependencyVisualizer.visualizeGraph(splitResult.subtasks, {
          showCriticalPath: true,
          criticalPath: splitResult.criticalPath
        }));
        return;
      }
      
      if (options.format === 'mermaid') {
        console.log(DependencyVisualizer.visualizeMermaid(splitResult.subtasks, {
          showCriticalPath: true,
          criticalPath: splitResult.criticalPath
        }));
        return;
      }
      
      // Text format
      console.log(pc.bold('\n📋 Task Breakdown:\n'));
      console.log(pc.cyan(`Original: ${splitResult.originalTask.content}`));
      console.log(`Total Time: ${formatDuration(splitResult.totalTime)}`);
      console.log(`Subtasks: ${splitResult.taskCount}`);
      console.log(`Max Depth: ${splitResult.maxDepth}`);
      
      // Display subtasks with dependencies
      console.log(pc.bold('\n🔗 Subtasks:\n'));
      
      splitResult.subtasks.forEach((subtask, index) => {
        const indent = '  '.repeat(subtask.dependencies?.length || 0);
        const deps = subtask.dependencies?.length ? 
          pc.dim(` [depends on: ${subtask.dependencies.join(', ')}]`) : '';
        const critical = splitResult.criticalPath.includes(subtask.id) ? 
          pc.red(' ⚡ CRITICAL') : '';
        
        console.log(`${indent}${index + 1}. ${subtask.title}${critical}`);
        console.log(`${indent}   Type: ${subtask.type}, Priority: ${subtask.priority}`);
        console.log(`${indent}   Time: ${formatDuration(subtask.estimatedTime)}${deps}`);
        console.log();
      });
      
      // Show critical path
      if (splitResult.criticalPath.length > 0) {
        console.log(pc.bold('⚡ Critical Path:'));
        const criticalTasks = splitResult.criticalPath
          .map(id => splitResult.subtasks.find(st => st.id === id))
          .filter(Boolean);
        
        const criticalTime = criticalTasks.reduce((sum, task) => sum + (task?.estimatedTime || 0), 0);
        console.log(`   ${criticalTasks.map(t => t?.title).join(' → ')}`);
        console.log(`   Total: ${formatDuration(criticalTime)}`);
      }
      
      // Gantt chart format
      if (options.format === 'gantt') {
        console.log(pc.bold('\n📊 Gantt Chart:\n'));
        
        // Simple text-based Gantt chart
        const scale = 60; // 1 character = 60 minutes
        const maxWidth = 50;
        
        splitResult.subtasks.forEach((subtask) => {
          const startTime = manager.calculateStartTime(subtask.id, splitResult.subtasks);
          const duration = subtask.estimatedTime;
          const startChars = Math.floor(startTime / scale);
          const durationChars = Math.max(1, Math.floor(duration / scale));
          
          const bar = '█'.repeat(Math.min(durationChars, maxWidth - startChars));
          const padding = ' '.repeat(startChars);
          const critical = splitResult.criticalPath.includes(subtask.id) ? pc.red(bar) : pc.green(bar);
          
          console.log(`${subtask.id.padEnd(3)} ${padding}${critical} ${subtask.title}`);
        });
        
        console.log('\n' + pc.dim('Scale: 1 char = 1 hour'));
      }
      
      // Save option
      if (options.save) {
        const saved = await manager.saveSubtasks(splitResult);
        if (saved) {
          console.log(pc.green('\n✅ Subtasks saved to workflow'));
        } else {
          console.log(pc.red('\n❌ Failed to save subtasks'));
        }
      }
    }));
  
  // Estimate task time
  command
    .command('estimate')
    .description('Estimate time for tasks')
    .argument('[task]', 'Task description to estimate')
    .option('-f, --file <file>', 'Estimate tasks from file (one per line)')
    .option('-d, --detailed', 'Show detailed analysis', false)
    .option('--format <format>', 'Output format (text/json)', 'text')
    .action(withErrorHandling(async (taskDesc, options) => {
      const manager = new WorkflowManager();
      
      // Collect tasks to estimate
      let tasks: string[] = [];
      
      if (options.file) {
        // Read tasks from file
        const { readFile } = await import('fs/promises');
        try {
          const content = await readFile(options.file, 'utf-8');
          tasks = content.split('\n')
            .map(line => line.trim())
            .filter(line => line && !line.startsWith('#')); // Skip empty lines and comments
        } catch (error) {
          console.log(pc.red(`Failed to read file: ${options.file}`));
          return;
        }
      } else if (taskDesc) {
        tasks = [taskDesc];
      } else {
        console.log(pc.red('Please provide a task description or use -f to specify a file'));
        return;
      }
      
      if (tasks.length === 0) {
        console.log(pc.yellow('No tasks to estimate'));
        return;
      }
      
      // Estimate each task
      const results = [];
      console.log(pc.bold('\n⏱️  Task Time Estimation:\n'));
      
      for (const task of tasks) {
        const estimation = await manager.estimateTaskTime(task);
        
        if (estimation) {
          const result = {
            task,
            complexity: estimation.complexity?.score || 0,
            estimatedTime: estimation.complexity?.estimatedTime || 0,
            type: estimation.workflowRecommendation?.type || 'unknown',
            factors: estimation.complexity?.factors || []
          };
          
          results.push(result);
          
          if (options.format === 'json') {
            continue; // Collect all results for JSON output
          }
          
          // Text output
          console.log(pc.cyan(`📌 ${task}`));
          console.log(`   Type: ${result.type}`);
          console.log(`   Complexity: ${result.complexity}/100`);
          console.log(`   Estimated time: ${formatDuration(result.estimatedTime)}`);
          
          if (options.detailed && result.factors.length > 0) {
            console.log(pc.dim('   Factors:'));
            result.factors.forEach(factor => {
              console.log(pc.dim(`     • ${factor}`));
            });
          }
          
          console.log(); // Empty line between tasks
        }
      }
      
      if (options.format === 'json') {
        console.log(JSON.stringify(results, null, 2));
      } else if (tasks.length > 1) {
        // Summary for multiple tasks
        const totalTime = results.reduce((sum, r) => sum + r.estimatedTime, 0);
        const avgComplexity = Math.round(results.reduce((sum, r) => sum + r.complexity, 0) / results.length);
        
        console.log(pc.bold('📊 Summary:'));
        console.log(`   Total tasks: ${results.length}`);
        console.log(`   Average complexity: ${avgComplexity}/100`);
        console.log(`   Total estimated time: ${formatDuration(totalTime)}`);
      }
    }));

  return command;
}